import { Bot, Building, Library, PlugZap, Workflow, type LucideIcon } from 'lucide-react';

// Namensregistry, kein UI-Shim: bildet gespeicherte Fluent-Icon-Namen auf
// lucide-Komponenten ab und fällt auf die lucide-Registry zurück.
import { fluentIconComponent } from '@/components/fluent-icon-map';
import { cn } from '@/lib/utils';

const TYPE_DEFAULT_ICONS: Record<string, LucideIcon> = {
    AgentToolset: PlugZap,
    KnowledgeBase: Library,
    PromptTool: Bot,
    AgenticWorkflow: Workflow,
};

/**
 * Fluent-Altnamen, die `fluentIconComponent` NICHT kennt (weder in
 * `FLUENT_ICON_MAP` noch in lucide) — bisher zeigten Angebote mit diesen
 * Namen gar kein Symbol.
 */
const LEGACY_ICON_NAMES: Record<string, LucideIcon> = {
    ChatBot: Bot,
    Flow: Workflow,
};

function getIconForOffer(offer: { icon?: string; _type?: string }): LucideIcon | null {
    const originalIcon = offer.icon;
    if (originalIcon?.toLowerCase() === 'building') {
        return Building;
    }
    if (originalIcon) {
        // Die Registry deckt die gespeicherten Fluent-Namen (CityNext,
        // PlugConnected, Library …) und die heutigen lucide-Namen ab.
        return (fluentIconComponent(originalIcon) as LucideIcon | null)
            ?? LEGACY_ICON_NAMES[originalIcon]
            ?? null;
    }
    return TYPE_DEFAULT_ICONS[offer._type || ''] ?? Building;
}

/** The offer's own icon in its offer color; falls back to the icon of its type. */
export function MarketplaceOfferIcon({ offer, className, neutral = false }: {
    offer: { icon?: string; iconColor?: string; _type?: string };
    className?: string;
    /** Without an own icon color: foreground instead of the tenant's primary color. */
    neutral?: boolean;
}) {
    const OfferIcon = getIconForOffer(offer);
    if (!OfferIcon) {
        return null;
    }
    const iconColor = offer.iconColor;
    return (
        <OfferIcon
            aria-hidden
            className={cn(!iconColor && (neutral ? 'text-foreground' : 'text-primary'), className)}
            style={iconColor ? { color: iconColor } : undefined}
        />
    );
}

export interface MarketplaceOfferNameCellProps {
    offer: { name: string; icon?: string; iconColor?: string; _type?: string };
}

/** Renders the Name column cell for marketplace offer tables: icon (in offer color) preceding the name. */
export function MarketplaceOfferNameCell({ offer }: MarketplaceOfferNameCellProps) {
    const OfferIcon = getIconForOffer(offer);
    const iconColor = offer.iconColor;
    return (
        <div className="flex items-center gap-2">
            {OfferIcon ? (
                <OfferIcon
                    aria-hidden
                    className={cn('h-4 w-4 shrink-0', !iconColor && 'text-primary')}
                    style={iconColor ? { color: iconColor } : undefined}
                />
            ) : (
                // Keeps the names of offers without an icon in line with the others.
                <span aria-hidden className="h-4 w-4 shrink-0" />
            )}
            <span className="min-w-0">{offer.name}</span>
        </div>
    );
}
